import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  IconClipboardFilled,
  IconMapPin,
  IconFileDescription,
  IconCurrencyDollar,
  IconInfoCircle,
  IconExternalLink,
  IconAlignLeft,
  IconUser,
  IconFileText,
} from "@tabler/icons-react";

import { useAppDispatch, useAppSelector } from "../../../store";
import {
  createServiceRequestPrice,
  getServiceRequest,
  updateServiceRequestPrice,
} from "../../../service/api/requests";
import { Loader } from "../../../components/animation/Loader";
import { formatDateTime } from "../../../service/convert/date";
import { useMediaQuery } from "../../../hooks/useMediaQuery";
import { IPrice } from "../../../store/@types/requests";
import { FieldRow } from "../misc/FieldRow";
import { TimelineItem } from "../misc/TimelimeItem";
import { Pill } from "../misc/Pill";
import { SectionCard } from "../misc/SectionCard";
import { PriceCard } from "../misc/PriceCard";
import { twMerge } from "tailwind-merge";

// ─── Helpers ──────────────────────────────────────────────────────────────────
// to b moved into a separate file

const getStatusConfig = (status: string | null) => {
  switch (status) {
    case "approved":
      return {
        label: "Approved",
        dot: "bg-primary-500",
        badge: "bg-primary-50/10 text-primary-800 border border-primary-200",
      };
    case "rejected":
      return {
        label: "Rejected",
        dot: "bg-red-500",
        badge: "bg-red-50 text-red-800 border border-red-200",
      };
    case "in_review":
      return {
        label: "In Review",
        dot: "bg-blue-500",
        badge: "bg-blue-50 text-blue-800 border border-blue-200",
      };
    default:
      return {
        label: "Pending Review",
        dot: "bg-amber-500",
        badge:
          "bg-amber-50/40 backdrop-blur-md text-amber-500 border border-amber-200",
      };
  }
};

const getInitials = (name: string) =>
  name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

// ─── Main Component ───────────────────────────────────────────────────────────

const RequestDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [imgError, setImgError] = React.useState(false);
  const [priceValue, setPriceValue] = React.useState("");
  const [priceError, setPriceError] = React.useState("");
  const [editingPriceId, setEditingPriceId] = React.useState<string | null>(
    null,
  );
  const [agreementNote, setAgreementNote] = React.useState("");

  const item = useAppSelector(({ serviveRequest }) => serviveRequest?.request);
  const loading = useAppSelector(
    ({ serviveRequest }) => serviveRequest?.loading,
  );
  const priceLoading = useAppSelector(
    ({ serviveRequest }) => serviveRequest?.priceLoading,
  );

  const isMobile = useMediaQuery("(max-width: 768px)");

  React.useEffect(() => {
    if (id) getServiceRequest(dispatch, id);
  }, [id, dispatch]);

  const resetPriceForm = () => {
    setPriceValue("");
    setPriceError("");
  };

  const validatePrice = (value: string) => {
    const numericPrice = Number(value);
    if (!value.trim()) {
      return "Price is required.";
    }

    if (Number.isNaN(numericPrice) || numericPrice <= 0) {
      return "Enter a valid amount greater than 0.";
    }

    return "";
  };

  const handleAddPrice = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!id) return;

    const errorMessage = validatePrice(priceValue);
    setPriceError(errorMessage);
    if (errorMessage) return;

    const done = await createServiceRequestPrice(dispatch, {
      request_id: id,
      price: priceValue.trim(),
    });

    if (done) {
      resetPriceForm();
    }
  };

  const onStartEditPrice = (priceItem: IPrice) => {
    setEditingPriceId(priceItem.id.toString());
    setAgreementNote(priceItem.agreement_note ?? "");
  };

  const onCancelEditPrice = () => {
    setEditingPriceId(null);
    setAgreementNote("");
  };

  const handleUpdatePrice = async (
    event: React.FormEvent<HTMLFormElement>,
    priceId: string,
  ) => {
    event.preventDefault();

    const done = await updateServiceRequestPrice(dispatch, {
      price_id: priceId,
      agreement_note: agreementNote.trim(),
    });

    if (done) {
      onCancelEditPrice();
    }
  };

  if (loading) return <Loader />;

  if (!item) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-4xl mb-3 text-red-400">⚠</div>
          <h3 className="text-base font-medium text-gray-800 mb-1">
            Request not found
          </h3>
          <p className="text-sm text-gray-400 mb-4">
            This request may have been removed or doesn't exist.
          </p>
          <button
            onClick={() => navigate("/dashboard/requests")}
            className="px-4 py-2 bg-gray-900 text-white text-sm rounded-lg hover:bg-gray-700 transition-colors"
          >
            Back to Requests
          </button>
        </div>
      </div>
    );
  }

  const status = getStatusConfig(item.request_status || "");
  const hasAdditional =
    item.others || item.price?.length > 0 || item.payment_link || item.document;

  return (
    <div className="max-w-6xl min-h-screen mx-auto">
      {/* ── Hero image ── */}

      <div
        className={twMerge(
          "relative w-full max-h-72 overflow-hidden border-b border-gray-200/20 rounded-2xl shadow-sm",
          imgError && "border-red-500",
        )}
      >
        {item.service ? (
          <img
            loading="lazy"
            src={item.service.image}
            alt={item.service.name}
            className="w-full h-full object-cover"
            onError={() => setImgError(true)}
          />
        ) : (
          <div className="w-full h-48 flex items-center justify-center bg-gray-100 rounded-lg">
            <IconFileText size={48} className="text-gray-300" />
          </div>
        )}
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent" />
        {/* Title overlay */}
        <div className="flex items-center justify-between absolute bottom-0 left-0 right-0 px-5 pb-4">
          <div>
            <h1 className="text-xl font-semibold text-white mb-0.5">
              {item.service ? item.service.name : item?.document?.name}
            </h1>
            <p className="text-[12px] text-white/80 font-mono">
              REQ ·{" "}
              {item.service
                ? item.service.id.slice(0, 8).toUpperCase()
                : item.document?.id.slice(0, 8).toUpperCase()}
              {item.service
                ? item.created_at && ` · ${formatDateTime(item.created_at)}`
                : item.document?.created_at &&
                  ` · ${formatDateTime(item.document.created_at)}`}
            </p>
          </div>
          <span
            className={`inline-flex items-center gap-1.5 text-[12px] font-medium px-3 py-1.5 rounded-full ${status.badge}`}
          >
            <span className={`w-1.5 h-1.5 rounded-full ${status.dot}`} />
            {status.label}
          </span>
        </div>
      </div>

      {/* ── Body ── */}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
        {/* ── Left column  ── */}
        <div className="lg:col-span-2">
          {item.service && (
            <SectionCard
              title="Service"
              badge={
                <Pill color={item.service.status === "1" ? "green" : "gray"}>
                  {item.service.status === "1" ? "Active" : "Inactive"}
                </Pill>
              }
            >
              <FieldRow
                icon={<IconAlignLeft size={isMobile ? 14 : 16} />}
                label="Description"
                value={
                  <span
                    className="font-normal text-gray-900 text-xs md:text-sm"
                    dangerouslySetInnerHTML={{
                      __html: item.service.description,
                    }}
                  />
                }
              />

              <FieldRow
                icon={<IconClipboardFilled size={isMobile ? 14 : 16} />}
                label="Request type"
                value={
                  <Pill color="blue">
                    {item.type === "new" ? "New request" : "Renewal"}
                  </Pill>
                }
              />
              <FieldRow
                icon={<IconMapPin size={isMobile ? 14 : 16} />}
                label="Location"
                value={
                  <span>
                    {item.state.name},{" "}
                    <span className="capitalize text-sm">
                      {item.state.country.name}
                    </span>
                  </span>
                }
              />
            </SectionCard>
          )}

          {/* documents */}
          {item.document && (
            <SectionCard
              title="Document"
              badge={
                <Pill color={item.document.status === "1" ? "green" : "gray"}>
                  {item.document.status === "1" ? "Active" : "Inactive"}
                </Pill>
              }
            >
              <FieldRow
                icon={<IconClipboardFilled size={isMobile ? 14 : 16} />}
                label="Request type"
                value={
                  <Pill color="blue">
                    {item.type === "new" ? "New request" : "Renewal"}
                  </Pill>
                }
              />
              <FieldRow
                icon={<IconMapPin size={isMobile ? 14 : 16} />}
                label="Location"
                value={
                  <span>
                    {item.state.name},{" "}
                    <span className="capitalize text-sm">
                      {item.state.country.name}
                    </span>
                  </span>
                }
              />
            </SectionCard>
          )}
          {hasAdditional && (
            <SectionCard title="Additional information">
              {item.others && (
                <FieldRow
                  icon={<IconInfoCircle size={isMobile ? 14 : 16} />}
                  label="Notes"
                  value={
                    <p className="font-normal text-sm text-gray-500 leading-relaxed border-l-2 border-amber-300 pl-3 mt-1">
                      {item.others}
                    </p>
                  }
                />
              )}
              {item.price && item.price.length > 0 && (
                <FieldRow
                  icon={<IconCurrencyDollar size={14} />}
                  label="Price details (Updating notes means you agree to the price offered)"
                  value={
                    <PriceCard
                      priceItem={item.price}
                      onStartEditPrice={onStartEditPrice}
                      onCancelEditPrice={onCancelEditPrice}
                      handleUpdatePrice={handleUpdatePrice}
                      onChange={(event) => setAgreementNote(event.target.value)}
                      editingPriceId={editingPriceId}
                      agreementNote={agreementNote}
                      priceLoading={priceLoading}
                    />
                  }
                />
              )}
              {item.payment_link && (
                <FieldRow
                  icon={<IconCurrencyDollar size={14} />}
                  label="Payment"
                  value={
                    <a
                      href={item.payment_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700 underline underline-offset-2"
                    >
                      View payment <IconExternalLink size={11} />
                    </a>
                  }
                />
              )}
              {item.document && (
                <FieldRow
                  icon={<IconFileDescription size={14} />}
                  label="Document"
                  value={
                    <a
                      href={item.document.path}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700 underline underline-offset-2"
                    >
                      View document <IconExternalLink size={11} />
                    </a>
                  }
                />
              )}
            </SectionCard>
          )}
        </div>

        {/* ── Right column ── */}
        <div>
          <SectionCard title="Submitted by">
            <div className="flex items-center gap-2.5 pb-3.5 mb-3.5 border-b border-gray-100/20">
              <div className="w-9 h-9 rounded-full bg-blue-50 flex items-center justify-center text-[12px] font-medium text-blue-700 flex-shrink-0">
                {item.user?.profile?.name ? (
                  getInitials(item.user.profile.name)
                ) : (
                  <IconUser size={14} className="text-blue-500" />
                )}
              </div>
              <div className="min-w-0">
                <p className="text-sm md:text-base font-medium text-gray-800 truncate">
                  {item.user?.profile?.name ?? "Unknown"}
                </p>
                <p className="text-xs text-gray-400 truncate">
                  {item.user?.email}
                </p>
              </div>
            </div>
            <div className="flex justify-between items-center text-[12px] mb-2">
              <span className="text-gray-400">Account</span>
              <Pill color={item.user?.status === "1" ? "green" : "gray"}>
                {item.user?.status === "1" ? "Active" : "Inactive"}
              </Pill>
            </div>
            <div className="flex justify-between items-center text-[12px]">
              <span className="text-gray-400">Role</span>
              <Pill color="blue">User</Pill>
            </div>
          </SectionCard>

          <SectionCard title="Request progress">
            <TimelineItem
              label="Submitted"
              sub={item.created_at ? formatDateTime(item.created_at) : "—"}
              state="done"
            />
            <TimelineItem
              label="Under review"
              sub="Awaiting admin action"
              state={
                item.request_status === "approved" ||
                item.request_status === "rejected"
                  ? "done"
                  : "active"
              }
            />
            <TimelineItem
              label="Decision"
              sub={
                item.request_status === "approved"
                  ? "Approved"
                  : item.request_status === "rejected"
                    ? "Rejected"
                    : "Approved or rejected"
              }
              state={
                item.request_status === "approved" ||
                item.request_status === "rejected"
                  ? "done"
                  : "pending"
              }
              last
            />
          </SectionCard>

          <SectionCard
            title="Counter
           price"
          >
            <form onSubmit={handleAddPrice} className="space-y-3">
              <div>
                <label className="text-xs font-medium text-gray-500">
                  Add price amount
                </label>
                <input
                  type="text"
                  value={priceValue}
                  onChange={(event) => {
                    setPriceValue(event.target.value);
                    if (priceError) setPriceError("");
                  }}
                  placeholder="e.g. 250000"
                  className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-200"
                />
                {priceError && (
                  <p className="text-xs text-red-500 mt-1">{priceError}</p>
                )}
              </div>
              <button
                type="submit"
                disabled={priceLoading}
                className="w-full px-3 py-2 rounded-md bg-gray-900 text-white text-sm hover:bg-gray-700 transition-colors"
              >
                {priceLoading ? "Adding..." : "Add price"}
              </button>
            </form>
          </SectionCard>
        </div>
      </div>
    </div>
  );
};

export default RequestDetail;
